import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	ReactNode,
	useCallback,
} from "react";
import { useLocation } from "@reach/router";
import { DisplayRfx, Rfx } from "../types/rfx.types";
import { isRfxOnboardingType } from "../utils";
import { getRfxById } from "../api/rfx";
import { RfxTabTotals } from "../types/component-types";
import { getTabCounts } from "../Submission/components/ViewSubmission/useRfxTabMenu";

interface RfxDataContextType {
	hasRfxLoaded: boolean;
	isOnboarding: boolean;
	mode: "api" | "create-preview"; // api is default retrieval from API, create-preview allows you to set own data for preview only
	rfxData: DisplayRfx | null;
	rfxId: number | null;
	rfxTabTotals: RfxTabTotals;

	// Setters (rarely needed)
	reloadRfxData: () => void;
	setPreviewData: (data: Rfx | null) => void;
	setRfxTabTotals: (tabTotals: RfxTabTotals) => void;
	updateLocalRfxData: (newRfxData: Rfx | null) => void;
}

const RfxDataContext = createContext<RfxDataContextType | null>(null);

export function RfxDataProvider({
	rfxRouteId,
	children,
}: {
	children: ReactNode;
	rfxRouteId: number;
}) {
	const { state } = useLocation() as any;
	// const params = useParams<{ rfxId?: string }>();

	/**
	 * URL route ID takes priority unless preview mode is active
	 */
	const apiId = rfxRouteId ? Number(rfxRouteId) : null;

	/**
	 * Optional preview data passed via router state
	 * navigate("/preview", { state: { previewData } })
	 */
	const previewDataFromRoute: Rfx | null = state?.previewData ?? null;

	const [previewData, setPreviewData] = useState<Rfx | null>(
		previewDataFromRoute
	);

	const [rfxData, setRfx] = useState<DisplayRfx | null>(null);
	const [rfxId, setRfxId] = useState<number | null>(apiId);
	const [hasFailedToLoadId, setHasFailedToLoadId] = useState<number | null>();
	const [isLoadingRfx, setIsLoadingRfx] = useState<boolean>(false);
	const [loadedRfx, setLoadedRfx] = useState<number>(0);
	const [rfxTabTotals, setRfxTabTotals] = useState<RfxTabTotals>(
		getTabCounts(
			rfxData,
			rfxData?.questionnaire?.length,
			rfxData?.quotes?.length
		)
	);

	const mode: "api" | "create-preview" = previewData ? "create-preview" : "api";

	const generateDisplayData = (rfxData: Rfx) => {
		if (!rfxData) {
			return rfxData as DisplayRfx;
		}

		return {
			...rfxData,
			displayName: rfxData.project?.name + " | " + rfxData.supplierList?.name,
		} as DisplayRfx;
	};

	const reloadRfxData = () => {
		setLoadedRfx(0);
	};

	const updateLocalRfxData = useCallback((rfxData: Rfx) => {
		setLoadedRfx(rfxData?.id ?? 0);
		setRfx(generateDisplayData(rfxData));
		setRfxId(rfxData?.id);
		setRfxTabTotals({
			addendums: rfxData?.addendum_count ?? 0,
			clarifications: rfxData?.clarifications_count ?? 0,
			comms: rfxData?.comms_count ?? 0,
			documentation: rfxData?.documentationDocument?.length ?? 0,
			evaluation: 0,
			quote: 1, // TODO: need to check how this is calculated
			returnableSchedule: 1, // TODO: need to check how this is calculated
		});
	}, []);

	useEffect(() => {
		if (mode === "create-preview" && previewData) {
			setRfx(generateDisplayData(previewData));
			setRfxId(previewData?.id ?? null);
		}
	}, [mode, previewData]);

	// ────────────────────────────────────────────
	// LOAD FROM API MODE (view/edit/preview)
	// ────────────────────────────────────────────
	useEffect(() => {
		if (
			!rfxId ||
			!apiId ||
			isLoadingRfx ||
			loadedRfx === rfxId ||
			mode === "create-preview" ||
			hasFailedToLoadId === rfxId
		) {
			return;
		}

		setIsLoadingRfx(true);

		getRfxById(rfxId)
			.then((rfxData) => {
				if (!rfxData) {
					// TODO: need do something nice for user here
					setHasFailedToLoadId(rfxId);
					return;
				}

				updateLocalRfxData(rfxData);
			})
			.finally(() => setIsLoadingRfx(false));
	}, [
		mode,
		apiId,
		hasFailedToLoadId,
		isLoadingRfx,
		rfxId,
		loadedRfx,
		updateLocalRfxData,
	]);

	// ────────────────────────────────────────────
	// 3. Derived state
	// ────────────────────────────────────────────
	const isOnboarding = useMemo(() => {
		if (!rfxData) return false;
		return isRfxOnboardingType(rfxData.rfxType || rfxData.type);
	}, [rfxData]);

	return (
		<RfxDataContext.Provider
			value={{
				mode,
				rfxData,
				rfxId,
				hasRfxLoaded: loadedRfx !== 0,
				isOnboarding,
				rfxTabTotals,
				reloadRfxData,
				setPreviewData,
				setRfxTabTotals,
				updateLocalRfxData,
			}}
		>
			{children}
		</RfxDataContext.Provider>
	);
}

export function useRfxData(
	ignoreContextCheck = false
): RfxDataContextType | null {
	const context = useContext(RfxDataContext);
	if (!ignoreContextCheck && !context) {
		throw new Error("useRfxData must be used inside <RfxDataProvider>");
	}
	return context;
}
